import { cva, VariantProps } from 'class-variance-authority';

const switchClassNames = cva(
    'tw-relative tw-inline-flex tw-mt-0 tw-h-4 tw-w-8 tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-accents-5 tw-transition-colors focus-visible:tw-outline-none',
    {
        variants: {
            disabled: {
                true: 'tw-cursor-not-allowed tw-bg-accents-2 tw-opacity-50',
                false: 'tw-cursor-pointer'
            },
            checked: {
                true: '',
                false: 'tw-bg-accents-3'
            },
            color: {
                primary: '',
                warning: '',
                error: '',
                success: ''
            },
            // TODO implement size variants
            size: {
                sm: '',
                md: '',
                lg: '',
                xl: ''
            }
        },
        compoundVariants: [
            {
                color: 'warning',
                checked: true,
                className: 'tw-bg-warning'
            },
            {
                color: 'error',
                checked: true,
                className: 'tw-bg-error'
            },
            {
                color: 'success',
                checked: true,
                className: 'tw-bg-success'
            }
        ],
        defaultVariants: {
            disabled: false,
            checked: false,
            color: 'success',
            size: 'md'
        }
    }
);

interface InlineSwitchProps extends VariantProps<typeof switchClassNames> {
    checked: boolean;
    className?: string;
    disabled?: boolean;
    onChange: (value: boolean, event: { target: { checked: boolean } }) => void;
    /**
     * What this switch is called, where no visible text sits beside it.
     *
     * A column of switches in a list needs its heading once, not on every row — but the
     * heading is not programmatically attached to anything, so without this each switch
     * announces itself as an unnamed control.
     */
    'aria-label'?: string;
    /** @deprecated */
    initialChecked?: boolean;
}

export const InlineSwitch = ({
    checked,
    className = '',
    disabled = false,
    onChange,
    color,
    size,
    'aria-label': ariaLabel
}: InlineSwitchProps) => {
    const toggle = () => {
        if (disabled) return;

        const next = !checked;

        const event = {
            target: { checked: next }
        };

        onChange(next, event);
    };

    return (
        <button
            type="button"
            role="switch"
            aria-label={ariaLabel}
            aria-checked={checked}
            aria-disabled={disabled}
            onClick={toggle}
            disabled={disabled}
            className={switchClassNames({ checked, disabled, className, color, size })}
        >
            <span
                className={`tw-inline-block tw-h-3 tw-w-3 tw-rounded-full tw-bg-white tw-transition-all ${checked ? 'tw-translate-x-2.5' : '-tw-translate-x-1'} `}
            />
        </button>
    );
};
